import { useNavigate, Link } from "react-router-dom";
import { Brain, ArrowLeft, FileText } from "lucide-react";

function PolicyLayout({ title, icon: Icon, children }) {
  const navigate = useNavigate();
  return (
    <div className="marketing-bg min-h-screen">
      <nav className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm font-dm-sans">
            <ArrowLeft size={16} /> Back to Home
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Brain size={16} className="text-[#00F0FF]" />
            <span className="font-outfit font-bold text-[#00F0FF] text-sm">ShansoAI<span className="text-white">Hub</span></span>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-[#00F0FF]/10 flex items-center justify-center">
            <Icon size={20} className="text-[#00F0FF]" strokeWidth={1.5} />
          </div>
          <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">ShansoAI Hub</span>
        </div>
        <h1 className="font-outfit font-black text-4xl sm:text-5xl text-white mb-3">{title}</h1>
        <p className="text-[#A1A1AA] text-sm font-dm-sans mb-10">Last updated: {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
        <div className="space-y-6 text-[#C0C0C0] font-dm-sans leading-relaxed">{children}</div>
      </div>
      <footer className="border-t border-[#27272A] py-6 px-6 mt-10">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <p className="text-[#A1A1AA] text-xs font-dm-sans">© {new Date().getFullYear()} ShansoAI Hub. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Privacy Policy</Link>
            <Link to="/terms" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Terms</Link>
            <Link to="/refund-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Refund Policy</Link>
            <Link to="/contact" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 p-6">
      <h2 className="font-outfit font-bold text-white text-xl mb-3">{title}</h2>
      <div className="text-[#A1A1AA] text-sm leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

export default function TermsConditions() {
  return (
    <PolicyLayout title="Terms & Conditions" icon={FileText}>
      <Section title="1. Acceptance of Terms">
        <p>By registering for any training program offered by ShansoAI Hub, you agree to be bound by these Terms and Conditions. If you do not agree, please refrain from registering or using our services.</p>
      </Section>
      <Section title="2. Training Enrollment">
        <ul className="list-disc list-inside space-y-1">
          <li>Registration is confirmed only upon successful payment via Cashfree Payment Gateway.</li>
          <li>Each seat is allocated on a first-come, first-served basis.</li>
          <li>ShansoAI Hub reserves the right to cancel or reschedule a training session with prior notice.</li>
          <li>Participants must be at least 18 years of age or have parental consent.</li>
        </ul>
      </Section>
      <Section title="3. Participant Conduct">
        <p>All participants are expected to:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Attend sessions punctually and participate respectfully</li>
          <li>Not share, reproduce, or distribute course materials without written permission</li>
          <li>Maintain confidentiality of any proprietary content shared during training</li>
          <li>Refrain from disruptive behavior during sessions</li>
        </ul>
      </Section>
      <Section title="4. Intellectual Property">
        <p>All training materials, slides, recordings, and content provided by ShansoAI Hub are the intellectual property of ShansoAI Hub. Unauthorized reproduction, distribution, or commercial use is strictly prohibited.</p>
      </Section>
      <Section title="5. Certificates">
        <p>Certificates of Completion are issued solely at the discretion of the admin/instructor upon satisfactory completion of the training program. Certificates are non-transferable and issued only to the registered participant.</p>
      </Section>
      <Section title="6. Limitation of Liability">
        <p>ShansoAI Hub shall not be liable for:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Any indirect, incidental, or consequential damages arising from participation</li>
          <li>Technical issues beyond our control (internet outages, platform downtime)</li>
          <li>Outcomes or employment results following the training</li>
        </ul>
      </Section>
      <Section title="7. Payment Terms">
        <p>All fees are listed in Indian Rupees (INR) inclusive of applicable taxes. Payment must be completed in full at the time of registration. ShansoAI Hub uses Cashfree as its payment gateway, and all transactions are subject to Cashfree's terms of service.</p>
      </Section>
      <Section title="8. Privacy">
        <p>Your use of our services is also governed by our <Link to="/privacy-policy" className="text-[#00F0FF] hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference.</p>
      </Section>
      <Section title="9. Modifications">
        <p>ShansoAI Hub reserves the right to modify these Terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms.</p>
      </Section>
      <Section title="10. Governing Law">
        <p>These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Tamil Nadu, India.</p>
      </Section>
      <Section title="11. Contact">
        <p>For any questions regarding these Terms, please <Link to="/contact" className="text-[#00F0FF] hover:underline">contact us</Link> or email <a href="mailto:support@shansoai.com" className="text-[#00F0FF] hover:underline">support@shansoai.com</a></p>
      </Section>
    </PolicyLayout>
  );
}
